import {CoinAmounts, CoinAsset, CoinMetadata, SuiStructTag, Vault, VaultBalance, CoinBalance} from "../types";
import { handleMessageError, VaultsErrorCode } from "../errors";
import BN from "bn.js";
import { d, TickMath } from "./tickMath";
import { MathUtil } from "./math";
import Decimal from "decimal.js";
import { normalizeSuiObjectId } from "@mysten/sui/utils";
import { GAS_TYPE_ARG, GAS_TYPE_ARG_LONG } from "../constant";
import { coinWithBalance, Transaction, TransactionObjectArgument } from "@mysten/sui/transactions";
import { Vaults } from "../modules/vaults";
import { estLiquidityAndCoinAmountFromOneAmounts } from "./est";

/**
 * Get token amount from liquidity.
 * @param liquidity - liquidity
 * @param cur_sqrt_price - Pool current sqrt price
 * @param lower_sqrt_price - position lower sqrt price
 * @param upper_sqrt_price - position upper sqrt price
 * @param round_up - is round up
 * @returns
 */
export function getCoinAmountFromLiquidity(
  liquidity: BN,
  cur_sqrt_price: BN,
  lower_sqrt_price: BN,
  upper_sqrt_price: BN,
  round_up: boolean
): CoinAmounts {
  const liq = new Decimal(liquidity.toString())
  const cur_sqrt_price_str = new Decimal(cur_sqrt_price.toString())
  const lower_price_str = new Decimal(lower_sqrt_price.toString())
  const upper_price_str = new Decimal(upper_sqrt_price.toString())
  let coin_a
  let coin_b
  if (cur_sqrt_price.lt(lower_sqrt_price)) {
    coin_a = MathUtil.toX64Decimal(liq).mul(upper_price_str.sub(lower_price_str)).div(lower_price_str.mul(upper_price_str))
    coin_b = new Decimal(0)
  } else if (cur_sqrt_price.lt(upper_sqrt_price)) {
    coin_a = MathUtil.toX64Decimal(liq).mul(upper_price_str.sub(cur_sqrt_price_str)).div(cur_sqrt_price_str.mul(upper_price_str))

    coin_b = MathUtil.fromX64Decimal(liq.mul(cur_sqrt_price_str.sub(lower_price_str)))
  } else {
    coin_a = new Decimal(0)
    coin_b = MathUtil.fromX64Decimal(liq.mul(upper_price_str.sub(lower_price_str)))
  }
  if (round_up) {
    return {
      coin_amount_a: coin_a.ceil().toString(),
      coin_amount_b: coin_b.ceil().toString(),
    }
  }
  return {
    coin_amount_a: coin_a.floor().toString(),
    coin_amount_b: coin_b.floor().toString(),
  }
}

/**
 * delta_liquidity = (lp_token_amount * total_liquidity_in_vault) / total_lp_amount
 * @param vault
 * @param current_amount
 * @returns
 */
export function getShareLiquidityByAmount(vault: Vault, current_amount: string) {
  if (vault.total_supply === '0') {
    return '0'
  }
  return d(current_amount).mul(vault.liquidity).div(vault.total_supply).toFixed(0, Decimal.ROUND_DOWN).toString()
}

export function extractStructTagFromType(type: string): SuiStructTag {
  try {
    let _type = type.replace(/\s/g, '')

    const genericsString = _type.match(/(<.+>)$/)
    const generics = genericsString?.[0]?.match(/(\w+::\w+::\w+)(?:<.*?>(?!>))?/g)
    if (generics) {
      _type = _type.slice(0, _type.indexOf('<'))
      const tag = extractStructTagFromType(_type)
      const structTag: SuiStructTag = {
        ...tag,
        type_arguments: generics.map((item) => extractStructTagFromType(item).source_address),
      }
      structTag.type_arguments = structTag.type_arguments.map((item) => {
        return extractStructTagFromType(item).full_address === GAS_TYPE_ARG ? item : extractStructTagFromType(item).source_address
      })
      structTag.source_address = composeType(structTag.full_address, structTag.type_arguments)
      return structTag
    }
    const parts = _type.split('::')

    const isSuiCoin = _type === GAS_TYPE_ARG || _type === GAS_TYPE_ARG_LONG

    const structTag: SuiStructTag = {
      full_address: _type,
      address: isSuiCoin ? '0x2' : normalizeSuiObjectId(parts[0]),
      module: parts[1],
      name: parts[2],
      type_arguments: [],
      source_address: '',
    }
    structTag.full_address = `${structTag.address}::${structTag.module}::${structTag.name}`
    structTag.source_address = composeType(structTag.full_address, structTag.type_arguments)
    return structTag
  } catch (error) {
    return {
      full_address: type,
      address: '',
      module: '',
      name: '',
      type_arguments: [],
      source_address: type,
    }
  }
}

export function composeType(address: string, generics: string[]): string
export function composeType(address: string, struct: string, generics?: string[]): string
export function composeType(address: string, module: string, struct: string, generics?: string[]): string
export function composeType(address: string, ...args: unknown[]): string {
  const generics: string[] = Array.isArray(args[args.length - 1]) ? (args.pop() as string[]) : []
  const chains = [address, ...args].filter(Boolean)

  let result: string = chains.join('::')

  if (generics && generics.length) {
    result += `<${generics.join(', ')}>`
  }

  return result
}

export function buildCoinWithBalance(amount: bigint, coin_type: string, tx: Transaction): TransactionObjectArgument {
  if (amount === BigInt(0)) {
    if (extractStructTagFromType(coin_type).full_address === GAS_TYPE_ARG) {
      return tx.add(coinWithBalance({ balance: amount, useGasCoin: false }))
    }
  }

  return tx.add(coinWithBalance({ balance: amount, type: coin_type }))
}

export function asIntN(int: bigint, bits = 32) {
  return Number(BigInt.asIntN(bits, BigInt(int)))
}

export function calculateDepositRatio(lower_tick: number, upper_tick: number, cur_sqrt_price: BN) {
  // Use a fixed amount of token A with proper decimals
  const coin_amount_a = new BN(100000000)
  const { coin_amount_b } = estLiquidityAndCoinAmountFromOneAmounts(
    lower_tick,
    upper_tick,
    coin_amount_a,
    true,
    true,
    0,
    cur_sqrt_price
  )

  const curr_price = TickMath.sqrtPriceX64ToPrice(cur_sqrt_price, 0, 0)

  const transform_amount_b = d(coin_amount_a.toString()).mul(curr_price)

  const total_amount = transform_amount_b.add(coin_amount_b.toString())
  const ratio_a = transform_amount_b.div(total_amount)
  const ratio_b = d(coin_amount_b.toString()).div(total_amount)

  return { ratio_a, ratio_b }
}

export function isSuiCoin(coin_address: string) {
  return extractStructTagFromType(coin_address).full_address === GAS_TYPE_ARG
}

export async function getSuiCoin(vaults: Vaults, amount: number, owner_address: string, tx?: Transaction): Promise<TransactionObjectArgument> {
  const allCoinAsset = await getOwnerCoinAssets(vaults, owner_address, GAS_TYPE_ARG)
  if (allCoinAsset.length === 0) {
    handleMessageError(VaultsErrorCode.InsufficientCoins, "No SUI coin objects found in wallet")
  }

  // 计算总余额
  const total = allCoinAsset.reduce((sum, coin) => sum + BigInt(coin.balance), BigInt(0))
  if (total < BigInt(amount)) {
    handleMessageError(VaultsErrorCode.InsufficientCoins, "Insufficient SUI balance")
  }

  tx = tx || new Transaction()
  // 合并所有 coin objects 到第一个
  const [firstCoin, ...otherCoins] = allCoinAsset
  if (otherCoins.length > 0) {
    tx.mergeCoins(
      tx.object(firstCoin.coin_object_id),
      otherCoins.map((coin) => tx.object(coin.coin_object_id))
    )
  }

  // 拆分出 amount 数量的 SUI
  const suiCoin = tx.splitCoins(tx.object(firstCoin.coin_object_id), [amount])
  return suiCoin
}

export async function getOwnerCoinAssets(vaults: Vaults, sui_address: string, coin_type?: string | null): Promise<CoinAsset[]> {
  const allCoinAsset: CoinAsset[] = []
  let nextCursor: string | null | undefined = null

  while (true) {
    const allCoinObject: any = await (coin_type
      ? vaults.getConfig().suiClient.getCoins({
        owner: sui_address,
        coinType: coin_type,
        cursor: nextCursor,
      })
      : vaults.getConfig().suiClient.getAllCoins({
        owner: sui_address,
        cursor: nextCursor,
      }))

    allCoinObject.data.forEach((coin: any) => {
      if (BigInt(coin.balance) > 0) {
        allCoinAsset.push({
          coin_type: extractStructTagFromType(coin.coinType).source_address,
          coin_object_id: coin.coinObjectId,
          balance: BigInt(coin.balance),
        })
      }
    })
    nextCursor = allCoinObject.nextCursor

    if (!allCoinObject.hasNextPage) {
      break
    }
  }
  return allCoinAsset
}

export function buildVaultBalance(
  wallet_address: string, vault: Vault, lp_token_balance: CoinBalance, coinMetadata: CoinMetadata, current_sqrt_price: string): VaultBalance {
  const liquidity = getShareLiquidityByAmount(vault, lp_token_balance.totalBalance)
  const { tick_lower_index, tick_upper_index, type_x, type_y } = vault.position
  const lower_sqrt_price = TickMath.tickIndexToSqrtPriceX64(tick_lower_index)
  const upper_sqrt_price = TickMath.tickIndexToSqrtPriceX64(tick_upper_index)
  const amount_info = getCoinAmountFromLiquidity(
    new BN(liquidity),
    new BN(current_sqrt_price),
    lower_sqrt_price,
    upper_sqrt_price,
    true
  )
  return {
    vault_id: vault.id,
    clmm_pool_id: vault.clmm_pool_id,
    owner: wallet_address,
    lp_token_type: vault.lp_token_type,
    lp_token_balance: lp_token_balance.totalBalance,
    lp_token_decimals: coinMetadata?.decimals ?? 9,
    liquidity,
    tick_lower_index,
    tick_upper_index,
    amount_a: amount_info.coin_amount_a.toString(),
    amount_b: amount_info.coin_amount_b.toString(),
    coin_type_a: type_x,
    coin_type_b: type_y,
    decimal_a: vault.decimals_a,
    decimal_b: vault.decimals_b,
  }
}

export function isCoinTypeEqual(coinTypeA: string, coinTypeB: string): boolean {
  if (isSuiCoin(coinTypeA) && isSuiCoin(coinTypeB)) {
    return true; // Both are SUI coins
  }
  return coinTypeA === coinTypeB; // Compare other coin types directly
}